/**
 * Superadmin is an explicit privilege layered on top of the admin role. Keep
 * this check strict: truthy strings or inherited role aliases must never make
 * privileged controls appear or allow the field into a request payload.
 */
export const canManageSuperadmins = (currentUser) =>
  currentUser?.userType === 'admin' && currentUser?.isSuperAdmin === true;

/**
 * Add the superadmin selection only when both the acting user and the target
 * role satisfy the UI security boundary. The API enforces the same rules, but
 * this prevents ordinary administrators from sending a stale/forged form
 * value in the first place.
 */
export const withSuperadminSelection = (
  payload,
  userData,
  currentUser,
  existingUser = null,
) => ({
  ...payload,
  ...(canManageSuperadmins(currentUser)
    && userData?.role === 'admin'
    && (!existingUser || existingUser.role === 'admin')
    && (!existingUser || existingUser.isSuperAdmin !== (userData.isSuperAdmin === true))
    ? { isSuperAdmin: userData.isSuperAdmin === true }
    : {}),
});

const USER_MUTATION_ERROR_KEYS = Object.freeze({
  SUPERADMIN_REQUIRED: 'admin.users.errors.superadminRequired',
  SUPERADMIN_REQUIRES_ADMIN_ROLE: 'admin.users.errors.adminRoleRequired',
  SUPERADMIN_REVOKE_REQUIRED: 'admin.users.errors.revokeBeforeRoleChange',
  SUPERADMIN_EXISTING_ADMIN_REQUIRED: 'admin.users.errors.existingAdminRequired',
  SUPERADMIN_PASSWORD_SETUP_REQUIRED: 'admin.users.errors.passwordSetupRequired',
  LAST_ACTIVE_SUPERADMIN: 'admin.users.errors.lastActiveSuperadmin',
});

/**
 * Translate only stable server codes. Backend messages can contain internal
 * validation details and are deliberately not rendered directly in this UI.
 */
export const userMutationErrorKey = (code, fallbackKey) =>
  USER_MUTATION_ERROR_KEYS[code] || fallbackKey;
